// src/components/onboarding/OnboardingModal.tsx

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "@/routing/accountRouting";
import { Box, Button, CloseButton, Group, Modal, Text } from "@mantine/core";

import { useOnboarding } from "@/context/OnboardingContext";
import OnboardingStepSidebar, { type StepInfo } from "./OnboardingStepSidebar";
import IndustryStep from "./steps/IndustryStep";
import { DwhSetupContent } from "@/components/dwh/DwhSetupModal";
import SourceSelectionStep from "./steps/SourceSelectionStep";
import GetStartedStep from "./steps/GetStartedStep";

const STEPS: StepInfo[] = [
  { key: "industry", label: "Choose your industry" },
  { key: "dwh", label: "Setup Data Warehouse" },
  { key: "sources", label: "Select your sources" },
  { key: "get_started", label: "Get Started" },
];

const STEP_INDEX: Record<string, number> = Object.fromEntries(
  STEPS.map((s, i) => [s.key, i])
);

export default function OnboardingModal() {
  const navigate = useNavigate();
  const {
    isModalOpen,
    closeModal,
    onboardingData,
    updateOnboarding,
    completeOnboarding,
  } = useOnboarding();

  // Local state
  const [currentStep, setCurrentStep] = useState(0);
  const [industry, setIndustry] = useState<string | null>(null);
  const [selectedSources, setSelectedSources] = useState<Set<string>>(new Set());
  // Display name per selected connector type, as the source step showed it
  // (the step reads its tiles from the connectors endpoint, so there is no
  // static list to look a name up in).
  const [sourceNames, setSourceNames] = useState<Record<string, string>>({});
  const [completedSteps, setCompletedSteps] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  // Initialize from server state
  useEffect(() => {
    if (!onboardingData?.onboarding_state) return;
    const state = onboardingData.onboarding_state;

    if (state.industry) setIndustry(state.industry);
    if (onboardingData.industry) setIndustry(onboardingData.industry);

    if (state.completed_steps) {
      setCompletedSteps(new Set(state.completed_steps));
    }

    if (state.current_step && STEP_INDEX[state.current_step] !== undefined) {
      setCurrentStep(STEP_INDEX[state.current_step]);
    }

    if (state.source_todos) {
      setSelectedSources(new Set(state.source_todos.map((t) => t.connector_type)));
      setSourceNames(
        Object.fromEntries(
          state.source_todos.map((t) => [t.connector_type, t.display_name ?? t.connector_type])
        )
      );
    }
  }, [onboardingData]);

  // Save state to backend
  const saveProgress = useCallback(
    async (step: string, extraCompleted?: string[]) => {
      setSaving(true);
      try {
        const newCompleted = new Set(completedSteps);
        if (extraCompleted) {
          extraCompleted.forEach((s) => newCompleted.add(s));
        }
        setCompletedSteps(newCompleted);

        await updateOnboarding({
          industry: industry || undefined,
          current_step: step,
          completed_steps: Array.from(newCompleted),
          source_todos:
            selectedSources.size > 0
              ? Array.from(selectedSources).map((ct) => ({
                  connector_type: ct,
                  display_name: sourceNames[ct] ?? ct,
                  status: "pending",
                }))
              : undefined,
        });
      } catch (err) {
        console.error("Failed to save onboarding progress:", err);
      } finally {
        setSaving(false);
      }
    },
    [completedSteps, industry, selectedSources, sourceNames, updateOnboarding]
  );

  // Navigation
  const goNext = useCallback(async () => {
    const currentKey = STEPS[currentStep].key;
    const nextIndex = currentStep + 1;

    if (nextIndex < STEPS.length) {
      await saveProgress(STEPS[nextIndex].key, [currentKey]);
      setCurrentStep(nextIndex);
    }
  }, [currentStep, saveProgress]);

  const goPrev = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep]);

  const handleSkipStep = useCallback(async () => {
    goNext();
  }, [goNext]);

  const handleSkipAll = useCallback(async () => {
    await completeOnboarding("SKIPPED");
  }, [completeOnboarding]);

  const handleContinueLater = useCallback(async () => {
    const currentKey = STEPS[currentStep].key;
    await saveProgress(currentKey);
    closeModal();
  }, [currentStep, saveProgress, closeModal]);

  const handleGetStarted = useCallback(async () => {
    await saveProgress("get_started", ["sources", "get_started"]);
    await completeOnboarding("COMPLETED");
    navigate("/setup/sources");
  }, [saveProgress, completeOnboarding, navigate]);

  // Industry select handler
  const handleIndustrySelect = useCallback((value: string) => {
    setIndustry(value);
  }, []);

  // Source toggle handler
  const handleSourceToggle = useCallback((connectorType: string, displayName: string) => {
    setSourceNames((prev) => ({ ...prev, [connectorType]: displayName }));
    setSelectedSources((prev) => {
      const next = new Set(prev);
      if (next.has(connectorType)) {
        next.delete(connectorType);
      } else {
        next.add(connectorType);
      }
      return next;
    });
  }, []);

  // DWH ready handler
  const handleDwhReady = useCallback(() => {
    updateOnboarding({
      dwh: { initiated: true },
    });
  }, [updateOnboarding]);

  // Selected source display names for summary
  const selectedSourceNames = useMemo(() => {
    return Array.from(selectedSources).map((ct) => sourceNames[ct] ?? ct);
  }, [selectedSources, sourceNames]);

  const canGoNext = useMemo(() => {
    switch (currentStep) {
      case 0: // Industry — optional, always can proceed
        return true;
      case 1: // DWH — always can proceed (non-blocking)
        return true;
      case 2: // Sources — optional
        return true;
      case 3: // Get Started — uses its own button
        return false;
      default:
        return true;
    }
  }, [currentStep]);

  // Render current step content
  const stepContent = useMemo(() => {
    switch (currentStep) {
      case 0:
        return (
          <IndustryStep selected={industry} onSelect={handleIndustrySelect} />
        );
      case 1:
        return <DwhSetupContent onReady={handleDwhReady} />;
      case 2:
        return (
          <SourceSelectionStep
            selected={selectedSources}
            onToggle={handleSourceToggle}
          />
        );
      case 3:
        return (
          <GetStartedStep
            industry={industry}
            dwhStatus={onboardingData?.dwh_provisioning_status ?? null}
            dwhProjectId={onboardingData?.dwh_project_id ?? null}
            dwhStorageMode={onboardingData?.dwh_storage_mode ?? null}
            selectedSources={selectedSourceNames}
            onGetStarted={handleGetStarted}
          />
        );
      default:
        return null;
    }
  }, [
    currentStep,
    industry,
    handleIndustrySelect,
    handleDwhReady,
    onboardingData,
    selectedSources,
    handleSourceToggle,
    selectedSourceNames,
    handleGetStarted,
  ]);

  return (
    <Modal
      opened={isModalOpen}
      onClose={() => {}} // Prevent close on overlay click
      closeOnClickOutside={false}
      closeOnEscape={false}
      withCloseButton={false}
      size={1100}
      radius="lg"
      padding={0}
      centered
      styles={{
        body: { padding: 0, height: "min(80vh, 650px)" },
        content: { overflow: "hidden" },
      }}
    >
      <Box style={{ display: "flex", height: "100%" }}>
        {/* Sidebar */}
        <Box
          style={{
            width: "33.333%",
            flexShrink: 0,
            backgroundColor: "var(--mantine-color-gray-0)",
            borderRight: "1px solid var(--mantine-color-gray-2)",
          }}
        >
          <OnboardingStepSidebar
            steps={STEPS}
            currentStep={currentStep}
            completedSteps={completedSteps}
            onStepClick={setCurrentStep}
          />
        </Box>

        {/* Content */}
        <Box
          p="xl"
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            position: "relative",
          }}
        >
          <CloseButton
            size="md"
            style={{ position: "absolute", top: 12, right: 12 }}
            onClick={handleContinueLater}
          />
          {/* Step content */}
          <Box style={{ flex: 1, minHeight: 0, overflow: "auto" }}>{stepContent}</Box>

          {/* Footer */}
          <Box
            pt="md"
            mt="md"
            style={{
              borderTop: "1px solid var(--mantine-color-gray-2)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
              <Group gap="sm">
                {currentStep > 0 && (
                  <Button variant="default" onClick={goPrev}>
                    Previous
                  </Button>
                )}
                {currentStep < STEPS.length - 1 && (
                  <Button onClick={goNext} disabled={!canGoNext} loading={saving}>
                    Next
                  </Button>
                )}
                {currentStep === STEPS.length - 1 && (
                  <Button onClick={handleGetStarted} loading={saving}>
                    Get Started
                  </Button>
                )}
              </Group>

              <Group gap="md">
                <Text
                  size="sm"
                  c="dimmed"
                  style={{ cursor: "pointer" }}
                  onClick={handleContinueLater}
                >
                  Continue later
                </Text>
                <Text
                  size="sm"
                  c="dimmed"
                  style={{ cursor: "pointer" }}
                  onClick={handleSkipAll}
                >
                  Skip all
                </Text>
                {currentStep < STEPS.length - 1 && (
                  <Text
                    size="sm"
                    c="dimmed"
                    style={{ cursor: "pointer" }}
                    onClick={handleSkipStep}
                  >
                    Skip
                  </Text>
                )}
              </Group>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}
